import { useEffect, useState } from 'react';

/**
 * Watches the given scroll container and flips a 'initial'/'scrolled'
 * state once the user crosses `threshold` pixels from the top. The
 * hook also resets the scroll position to zero whenever it attaches
 * (e.g. on song change) so the navbar starts in its initial state.
 *
 * Why take `ref` + `isReady` instead of just the ref?
 *   The scrollable DOM node mounts a tick after loading flips to false.
 *   Re-running the effect with `isReady` ensures we don't attach the
 *   listener against a `null` ref (symptom: navbar stuck in 'initial').
 */
const useScrollRevealNavbar = ({ ref, isReady, resetKey, threshold = 100 }) => {
  const [navbarState, setNavbarState] = useState('initial');

  useEffect(() => {
    if (!isReady) return undefined;
    const container = ref.current;
    if (!container) return undefined;

    const handleScroll = () => {
      const currentScroll = container.scrollTop || 0;
      if (currentScroll > threshold) {
        setNavbarState('scrolled');
      } else {
        setNavbarState('initial');
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.scrollTop = 0;
    setNavbarState('initial');

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [ref, isReady, resetKey, threshold]);

  return navbarState;
};

export default useScrollRevealNavbar;
